import { measureLatency } from '../../gctl-measure';
import { AlbumFields, Album } from '../../models/components/schemas/album/Album';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetUserAlbumsFields = readonly AlbumFields[];

/** Options for GET /users/{user_id}/albums */
export type GetUserAlbumsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Possible query parameters for /users/{user_id}/albums */
  query?: {
    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * A comma-separated list of showcase privacies to include. Valid options are `anybody` for universal access, `password` for password-restricted access, `embed_only` for restricted access on Vimeo but available as an embedded showcase elsewhere, `team` for team-only access, `nobody` for owner-only access, and `unlisted` for restricted access except for those with a direct link. By default, all privacies are returned.
     */
    filterPrivacy?: string;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;

    /**
     * Whether to preview custom settings.
     */
    preview?: boolean;

    /**
     * The search query to use to filter the results.
     */
    query?: string;

    /**
     * The way to sort the results.
     *
     * Option descriptions:
     *  * `alphabetical` - Sort the results alphabetically.
     *  * `date` - Sort the results by date of creation.
     *  * `duration` - Sort the results by duration.
     *  * `last_modified` - Sort the results by last modified date.
     *  * `videos` - Sort the results by the number of videos.
     *
     */
    sort?: `alphabetical` | `date` | `duration` | `last_modified` | `videos`;
  };
};

/** Result type for GET /users/{user_id}/albums */
export type GetUserAlbumsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /users/{user_id}/albums */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<Album, Paths>[];
};

/**
 * Get all the showcases that belong to the user
 * This method returns every showcase belonging to the authenticated user.
 */
export async function getUserAlbums<Paths extends GetUserAlbumsFields>({
  baseUrl,
  select,
  where: { userId },
  query,
  ...options
}: GetUserAlbumsOptions<Paths>): Promise<GetUserAlbumsResult<Paths>> {
  return measureLatency('getUserAlbums', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/users/${userId}/albums?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetUserAlbumsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostUserAlbumsFields = readonly AlbumFields[];

/** Options for POST /users/{user_id}/albums */
export type PostUserAlbumsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.album+json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Data serialized to body for POST /users/{user_id}/albums */
  variables: {
    /**
     * Whether the showcase permits continuous play.
     * @example true
     */

    allowContinuousPlay?: boolean;

    /**
     * Whether the showcase permits downloads.
     * @example true
     */

    allowDownloads?: boolean;

    /**
     * Whether the showcase permits sharing.
     * @example true
     */

    allowShare?: boolean;

    /**
     * The hexadecimal color code for the color of the player buttons and showcase controls.
     * @example ff66ee
     */

    brandColor?: string;

    /**
     * The description of the showcase.
     * @example Vimeo holiday videos!
     */

    description?: string;

    /**
     * Whether the showcase should be hidden from Vimeo when unlisted.
     */

    hideFromVimeo?: boolean;

    /**
     * Whether to hide Vimeo navigation when displaying the showcase.
     * @example true
     */

    hideNav?: boolean;

    /**
     * Whether to include the upcoming event in the showcase.
     */

    hideUpcoming?: boolean;

    /**
     * The type of layout for presenting the showcase.
     *
     * Option descriptions:
     *  * `grid` - The videos appear in a grid.
     *  * `player` - The videos appear in the player.
     *
     */

    layout?: `grid` | `player`;

    /**
     * The name of the showcase.
     * @example Vimeo Holiday Videos!
     */

    name: string;

    /**
     * The showcase's password. This field is required only when **privacy** is `password`.
     * @example hunter1
     */

    password?: string;

    /**
     * The privacy level of the showcase.
     *
     * Option descriptions:
     *  * `anybody` - Anyone can access the showcase, either on Vimeo or through an embed.
     *  * `embed_only` - The showcase doesn't appear on Vimeo, but it can be embedded on other sites.
     *  * `nobody` - No one can access the showcase, including the authenticated user.
     *  * `password` - Only those with the password can access the showcase.
     *  * `team` - Only members of the authenticated user's team can access the showcase.
     *  * `unlisted` - The showcase can't be accessed if the URL omits its unlisted hash.
     *
     */

    privacy?: `anybody` | `embed_only` | `nobody` | `password` | `team` | `unlisted`;

    /**
     * Whether showcase videos use the review mode URL.
     * @example true
     */

    reviewMode?: boolean;

    /**
     * The default sort order of the videos as they appear in the showcase.
     *
     * Option descriptions:
     *  * `added_first` - The videos appear according to when they were added to the showcase, with the most recently added first.
     *  * `added_last` - The videos appear according to when they were added to the showcase, with the most recently added last.
     *  * `alphabetical` - The videos appear alphabetically by their title.
     *  * `alphabetical_desc` - The videos appear alphabetically by their title in reverse order.
     *  * `arranged` - The videos appear as arranged by the owner of the showcase.
     *  * `comments` - The videos appear according to their number of comments.
     *  * `likes` - The videos appear according to their number of likes.
     *  * `modified_time_asc` - The videos appear in ascending order based on their last modified time.
     *  * `modified_time_desc` - The videos appear in descending order based on their last modified time.
     *  * `newest` - The videos appear in chronological order with the newest first.
     *  * `oldest` - The videos appear in chronological order with the oldest first.
     *  * `plays` - The videos appear according to their number of plays.
     *
     */

    sort?:
      | `added_first`
      | `added_last`
      | `alphabetical`
      | `alphabetical_desc`
      | `arranged`
      | `comments`
      | `likes`
      | `modified_time_asc`
      | `modified_time_desc`
      | `newest`
      | `oldest`
      | `plays`;

    /**
     * The color theme of the showcase.
     *
     * Option descriptions:
     *  * `dark` - The showcase uses the dark theme.
     *  * `standard` - The showcase uses the standard theme.
     *
     */

    theme?: `dark` | `standard`;

    /**
     * Whether to use the showcase's brand color in the web layout.
     * @example true
     */

    webBrandColor?: boolean;

    /**
     * Whether to use the showcase's custom logo in the web layout.
     * @example true
     */

    webCustomLogo?: boolean;
  };
};

/** Result type for POST /users/{user_id}/albums */
export type PostUserAlbumsResult<Paths extends readonly string[]> = Select<Album, Paths>;

/**
 * Create a showcase
 * This method creates a new showcase for the specified user.
 */
export async function postUserAlbums<Paths extends PostUserAlbumsFields>({
  baseUrl,
  select,
  variables,
  where: { userId },
  ...options
}: PostUserAlbumsOptions<Paths>): Promise<PostUserAlbumsResult<Paths>> {
  return measureLatency('postUserAlbums', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/users/${userId}/albums?fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostUserAlbumsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchUserAlbumsFields = readonly string[];

/** Options for PATCH /users/{user_id}/albums */
export type PatchUserAlbumsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Possible query parameters for /users/{user_id}/albums */
  query: {
    /**
     * A comma-separated list of video or event URIs.
     */
    albumItemUris: string;

    /**
     * A comma-separated list of showcase URIs.
     */
    albumUris: string;
  };
};

/** Result type for PATCH /users/{user_id}/albums */
export type PatchUserAlbumsResult<Paths extends readonly string[]> = unknown;

/**
 * Add videos and events to showcases
 * This method adds videos and events to the specified showcases. The authenticated user must either be the owner of the showcase or have team permissions.
 *
 * The present setup permits only one event per showcase.
 */
export async function patchUserAlbums<Paths extends PatchUserAlbumsFields>({
  baseUrl,
  where: { userId },
  query,
  ...options
}: PatchUserAlbumsOptions<Paths>): Promise<PatchUserAlbumsResult<Paths>> {
  return measureLatency('patchUserAlbums', 'PATCH', async () => {
    let res = await fetch(`${baseUrl}/users/${userId}/albums?${searchQueryString(query)}`, {
      ...options,
      method: 'PATCH',
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchUserAlbumsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
